import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import {
  addStoreApi,
  getStoreByIdApi,
  updateStoreApi,
} from "../services/store.service";
import { getCompanyApi } from "../services/company.service";
import { getActiveSuppliersApi } from "../services/supplier.service";
import { toast } from "react-toastify";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import dayjs from "dayjs";
import { getItemApi } from "../services/item.service";
import { DatePicker } from "@mui/x-date-pickers";
import StoreSchema from "../joi/store/store.schema";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function StoreAddStock() {
  const { id } = useParams();
  const navigate = useNavigate();

  const initialState = {
    item: "",
    company: "",
    supplier: "",
    batchNo: "",
    serialNo: "",
    expiryDate: null,
    quantity: 0,
    purchaseRate: 0,
    hospitalRate: 0,
    mrp: 0,
    isSterilized: false,
    // status: "",
    // updatedBy: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [isAutofill, setIsAutofill] = useState(false);
  const [errors, setErrors] = useState([])

  const {
    dataList: selectedRow,
    isLoading: selectedRowLoading,
    isError: selectedRowError,
  } = useGetApi(
    id
      ? { apiFunction: getStoreByIdApi, params: { id } }
      : { apiFunction: null, skip: true } // it is done to prevent the api call when I open it for pharmacy store creation
  );

  // companyList
  const { dataList: companyList } = useGetApi({
    apiFunction: getCompanyApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "companies",
  });

  // itemList
  const { dataList: itemList } = useGetApi({
    apiFunction: getItemApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "items",
  });

  // supplierList
  const { dataList: supplierList } = useGetApi({
    apiFunction: getActiveSuppliersApi,
    params: {},
    dataKey: "data",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((preValue) => ({
      ...preValue,
      [name]:
        type === "checkbox"
          ? checked
          : name === "quantity" ||
            name === "purchaseRate" ||
            name === "hospitalRate" ||
            name === "mrp"
          ? Number(value)
          : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    // Format expiryDate manually before validation
    const formattedData = {
      ...formData,
      company: formData?.company?._id,
      item: formData?.item?._id,
      supplier: formData?.supplier?.supplierName || formData?.supplier,
      expiryDate: formData?.expiryDate ? dayjs(formData.expiryDate) : null,
    };

    const { error } = StoreSchema.validate(formattedData, {abortEarly: false});

    if (error) {
      const errors =[];
      error.details.forEach((err) => errors.push(err?.context?.label));
      setErrors(errors);
      toast.error(
        `Please fill all the required fields.`
      );
      return;
    }

    setIsLoading(true);
    if (selectedRow?._id) {
      response = await updateStoreApi(formattedData);
    } else {
      response = await addStoreApi(formattedData);
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `Store ${selectedRow?._id ? "updated" : "added"} successfully`
      );
      if (selectedRow?._id) {
        navigate(-1);
      } else setFormData(initialState);
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  // Sync selectedRow data when modal opens
  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        item: selectedRow?.item || "",
        company: selectedRow?.company || "",
        supplier: supplierList?.find(s => s.supplierName === selectedRow?.supplier) || selectedRow?.supplier || "",
        batchNo: selectedRow?.batchNo || "",
        serialNo: selectedRow?.serialNo || "",
        expiryDate: selectedRow?.expiryDate
          ? dayjs(selectedRow?.expiryDate)
          : null,
        quantity: selectedRow?.quantity ?? 0,
        purchaseRate: selectedRow?.purchaseRate ?? 0,
        hospitalRate: selectedRow?.hospitalRate ?? 0,
        mrp: selectedRow?.mrp ?? 0,
        isSterilized: selectedRow?.isSterilized ?? false,
        // status: selectedRow?.status || "",
        // updatedBy: selectedRow?.updatedBy || "",
      });
    } else {
      setFormData(initialState);
    }
  }, [id, selectedRow, supplierList]);
  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        {selectedRowLoading ? (
          <Loader />
        ) : selectedRowError ? (
          <ErrorMessage />
        ) : (
          <Box
            component="form"
            sx={{ width: "100%", pr: "90px", py: "20px" }}
            onSubmit={handleSubmit}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: '#5D6A82', 
                textTransform: 'none', 
                fontSize: '16px',
                fontWeight: 'bold',
                padding: 0,
                transition: 'none !important',
                mb: 2
              }}
            >
              Back
            </Button>
            {/* enter stock details */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: "20px",
              }}
            >
              <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
                {`${selectedRow?._id ? "Update" : "Enter"} Stock Details`}
              </Typography>
              {/* <IconButton>
                <CloseIcon />
              </IconButton> */}
            </Box>

            {/* first box */}
            <Box
              sx={{
                backgroundColor: "rgba(240, 247, 252, 1)",
                borderRadius: "12px",
                mb: "30px",
                p: "20px",
              }}
            >
              <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                Basic Info
              </Typography>
              <Grid container spacing={2}>
                {/* itemCode */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={itemList || []}
                    getOptionLabel={(option) => option?.itemCode || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item Code"
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('item') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}
                        fullWidth
                      />
                    )}
                    value={formData?.item || ""}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "item", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* itemName */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={itemList || []}
                    getOptionLabel={(option) => option?.itemName || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Item Name"
                        // required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('item') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}
                        fullWidth
                      />
                    )}
                    value={formData?.item || ""}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "item", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* supplier */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={supplierList || []}
                    getOptionLabel={(option) => option?.supplierName || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Supplier"
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes("supplier") ? '1px solid red' : '1px solid inherit',
                            },
                          },
                        }}
                        fullWidth
                      />
                    )}
                    value={formData?.supplier || null}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "supplier", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* checkbox */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isAutofill"
                        checked={isAutofill}
                        onChange={(e) => setIsAutofill(e.target.checked)}
                      />
                    }
                    label="Autofill details from the previous stock entry for this Item Code."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* second box */}
            <Box
              sx={{
                backgroundColor: "rgba(240, 247, 252, 1)",
                borderRadius: "12px",
                mb: "30px",
                p: "20px",
              }}
            >
              <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                Other Details
              </Typography>
              <Grid container spacing={2} alignItems="center">
                {/* batchNo */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Batch No"
                    name="batchNo"
                    value={formData?.batchNo || ""}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      required: true,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: errors?.includes("batchNo") ? '1px solid red' : '1px solid inherit',
                        },
                      },
                      bgcolor: "white" 
                    }}
                  />
                </Grid>

                {/* serialNo */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Serial No"
                    name="serialNo"
                    value={formData?.serialNo || ""}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      required: true,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    fullWidth
                    // sx={{ bgcolor: "white" }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: errors?.includes("serialNo") ? '1px solid red' : '1px solid inherit',
                        },
                      },
                      bgcolor: "white" 
                    }}
                  />
                </Grid>

                {/* company */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={companyList || []}
                    getOptionLabel={(option) => option?.name || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company"
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                        }}
                        fullWidth
                      />
                    )}
                    value={formData?.company || ""}
                    onChange={(_, newValue) =>
                      handleChange({
                        target: { name: "company", value: newValue },
                      })
                    }
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* expiryDate */}
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label="Expiry Date"
                    disablePast
                    value={
                      formData?.expiryDate ? dayjs(formData?.expiryDate) : null
                    }
                    onChange={(newValue) =>
                      handleChange({
                        target: { name: "expiryDate", value: newValue },
                      })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                      },
                    }}
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* quantity */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Quantity"
                    name="quantity"
                    value={formData?.quantity || ""}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      required: true,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    fullWidth
                    sx={{ bgcolor: "white",
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: errors?.includes("quantity") ? '1px solid red' : '1px solid inherit',
                        },
                      },
                     }}
                    
                  />
                </Grid>

                {/* purchaseRate */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Purchase Rate"
                    name="purchaseRate"
                    value={formData?.purchaseRate || ""}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      required: true,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    fullWidth
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* mrp */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="MRP"
                    name="mrp"
                    value={formData?.mrp || ""}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      required: true,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    fullWidth
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* hospitalRate */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    type="number"
                    label="Hospital Rate"
                    name="hospitalRate"
                    value={formData?.hospitalRate || ""}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      required: true,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    fullWidth
                    sx={{ bgcolor: "white" }}
                  />
                </Grid>

                {/* isSterilized */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isSterilized"
                        checked={formData?.isSterilized}
                        onChange={handleChange}
                      />
                    }
                    label="Is Sterilized"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* save button */}
            <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  textTransform: "none",
                  backgroundColor: "rgba(1, 121, 176, 1)",
                  width: "150px",
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : selectedRow?._id ? (
                  "Update"
                ) : (
                  `Save`
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
}
